/* Hövding mega header
   - Hover intent + focus on nav items to open mega panels
   - Click/focus toggle for country/currency popover
   - Search panel reveal + auto-focus
   - Mobile drawer toggle
   - Escape + outside-click to close; keyboard nav between top-level items */

class HovMegaHeader extends HTMLElement {
  constructor() {
    super();
    this.hoverDelay = 80;
    this.closeDelay = 140;
    this._openTimer = null;
    this._closeTimer = null;
    this._activeItem = null;
  }

  connectedCallback() {
    this.navItems = Array.from(this.querySelectorAll('[data-hov-nav-item]'));
    this.scrim = this.querySelector('[data-hov-scrim]');

    this._wireNav();
    this._wireLocale();
    this._wireSearch();
    this._wireDrawer();
    this._wireGlobal();
    this._wireFloat();
    this._wireAnchorSpy();
  }

  /* -------- Anchor scroll-spy: underline the nav item whose section is in view -------- */

  _wireAnchorSpy() {
    if (!('IntersectionObserver' in window)) return;
    const items = new Map(); // section element -> nav <li>
    this.querySelectorAll('.hov-nav__trigger--link[href*="#"]').forEach((link) => {
      const hash = (link.getAttribute('href') || '').split('#')[1];
      if (!hash) return;
      const target = document.getElementById(hash);
      const item = link.closest('.hov-nav__item');
      if (target && item) items.set(target, item);
    });
    if (!items.size) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          items.forEach((item) => item.classList.remove('hov-nav__item--active'));
          items.get(entry.target)?.classList.add('hov-nav__item--active');
        });
      },
      /* a section is "active" while it crosses the upper-middle band of the viewport */
      { rootMargin: '-25% 0px -65% 0px' }
    );
    items.forEach((_, target) => io.observe(target));
  }

  /* -------- Float-over-hero: glass capsule until the hero scrolls past -------- */

  _wireFloat() {
    if (!this.classList.contains('hov-mega-header--float')) return;
    const hero = document.querySelector('.hov-hero');
    if (!hero) {
      this.classList.add('is-solid');
      return;
    }
    let ticking = false;
    const update = () => {
      ticking = false;
      const heroBottom = hero.getBoundingClientRect().bottom;
      this.classList.toggle('is-solid', heroBottom <= this.offsetHeight + 24);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* -------- Nav / mega panels -------- */

  _wireNav() {
    this.navItems.forEach((item) => {
      const trigger = item.querySelector('[data-hov-nav-trigger]');
      const mega = item.querySelector('[data-hov-mega]');
      if (!trigger || !mega) return;

      item.addEventListener('mouseenter', () => this._scheduleOpen(item));
      item.addEventListener('mouseleave', () => this._scheduleClose(item));

      trigger.addEventListener('focus', () => this._open(item));
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (item === this._activeItem) {
          this._close(item);
        } else {
          this._open(item);
        }
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this._open(item);
          const firstLink = mega.querySelector('a, button');
          firstLink?.focus();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          const idx = this.navItems.indexOf(item);
          const next = e.key === 'ArrowRight' ? idx + 1 : idx - 1;
          const target = this.navItems[next];
          if (target) {
            e.preventDefault();
            target.querySelector('[data-hov-nav-trigger]')?.focus();
          }
        }
      });

      mega.addEventListener('mouseenter', () => this._scheduleOpen(item));
      mega.addEventListener('mouseleave', () => this._scheduleClose(item));

      mega.addEventListener('focusout', (e) => {
        if (!mega.contains(e.relatedTarget) && !trigger.contains(e.relatedTarget)) {
          this._close(item);
        }
      });
    });
  }

  _scheduleOpen(item) {
    clearTimeout(this._closeTimer);
    clearTimeout(this._openTimer);
    if (this._activeItem && this._activeItem !== item) {
      this._close(this._activeItem);
    }
    this._openTimer = setTimeout(() => this._open(item), this.hoverDelay);
  }

  _scheduleClose(item) {
    clearTimeout(this._openTimer);
    clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => this._close(item), this.closeDelay);
  }

  _open(item) {
    if (this._activeItem && this._activeItem !== item) {
      this._close(this._activeItem);
    }
    const trigger = item.querySelector('[data-hov-nav-trigger]');
    const mega = item.querySelector('[data-hov-mega]');
    if (!mega) return;
    item.classList.add('hov-nav__item--open');
    mega.hidden = false;
    trigger?.setAttribute('aria-expanded', 'true');
    this._activeItem = item;
    this.setAttribute('data-mega-open', 'true');
  }

  _close(item) {
    const trigger = item.querySelector('[data-hov-nav-trigger]');
    const mega = item.querySelector('[data-hov-mega]');
    if (!mega) return;
    item.classList.remove('hov-nav__item--open');
    mega.hidden = true;
    trigger?.setAttribute('aria-expanded', 'false');
    if (this._activeItem === item) this._activeItem = null;
    if (!this._activeItem) this.removeAttribute('data-mega-open');
  }

  _closeAllMega() {
    this.navItems.forEach((item) => this._close(item));
  }

  /* -------- Locale popover -------- */

  _wireLocale() {
    const wrap = this.querySelector('[data-hov-locale]');
    if (!wrap) return;
    const toggle = wrap.querySelector('[data-hov-locale-toggle]');
    const panel = wrap.querySelector('[data-hov-locale-panel]');
    const select = wrap.querySelector('[data-hov-country]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      const open = !panel.hidden;
      if (open) {
        this._closeLocale();
      } else {
        this._openLocale();
      }
    });

    // Submit the localization form when the country changes.
    select?.addEventListener('change', () => {
      const form = select.closest('form');
      form?.submit();
    });

    this._locale = { wrap, toggle, panel };
  }

  _openLocale() {
    if (!this._locale) return;
    this._locale.panel.hidden = false;
    this._locale.toggle.setAttribute('aria-expanded', 'true');
  }

  _closeLocale() {
    if (!this._locale) return;
    this._locale.panel.hidden = true;
    this._locale.toggle.setAttribute('aria-expanded', 'false');
  }

  /* -------- Search panel -------- */

  _wireSearch() {
    const toggle = this.querySelector('[data-hov-search-toggle]');
    const panel = this.querySelector('[data-hov-search-panel]');
    const input = this.querySelector('[data-hov-search-input]');
    const closeBtn = this.querySelector('[data-hov-search-close]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      const open = !panel.hidden;
      if (open) {
        this._closeSearch();
      } else {
        this._openSearch();
        setTimeout(() => input?.focus(), 30);
      }
    });

    closeBtn?.addEventListener('click', () => {
      this._closeSearch();
      toggle.focus();
    });

    this._search = { toggle, panel, input };
  }

  _openSearch() {
    if (!this._search) return;
    this._search.panel.hidden = false;
    this._search.toggle.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-search-open', 'true');
  }

  _closeSearch() {
    if (!this._search) return;
    this._search.panel.hidden = true;
    this._search.toggle.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-search-open');
  }

  /* -------- Mobile drawer -------- */

  _wireDrawer() {
    const burger = this.querySelector('[data-hov-burger]');
    const drawer = this.querySelector('[data-hov-drawer]');
    const closeBtn = this.querySelector('[data-hov-drawer-close]');
    if (!burger || !drawer) return;

    burger.addEventListener('click', () => {
      const open = this.getAttribute('data-drawer-open') === 'true';
      if (open) {
        this._closeDrawer();
      } else {
        this._openDrawer();
      }
    });

    closeBtn?.addEventListener('click', () => {
      this._closeDrawer();
      burger.focus();
    });

    this._drawerEls = { burger, drawer };
  }

  _openDrawer() {
    if (!this._drawerEls) return;
    this._drawerEls.drawer.hidden = false;
    // Force reflow so the transform transition runs on first open.
    void this._drawerEls.drawer.offsetHeight;
    this.setAttribute('data-drawer-open', 'true');
    this._drawerEls.burger.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
  }

  _closeDrawer() {
    if (!this._drawerEls) return;
    this.removeAttribute('data-drawer-open');
    this._drawerEls.burger.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    // Delay `hidden` so the fade-out transition completes.
    setTimeout(() => {
      if (this.getAttribute('data-drawer-open') !== 'true') {
        this._drawerEls.drawer.hidden = true;
      }
    }, 480);
  }

  /* -------- Global listeners -------- */

  _wireGlobal() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this._closeAllMega();
        this._closeLocale();
        this._closeSearch();
        if (this.getAttribute('data-drawer-open') === 'true') {
          this._closeDrawer();
          this._drawerEls?.burger.focus();
        }
      }
    });

    document.addEventListener('click', (e) => {
      if (this._locale && !this._locale.panel.hidden) {
        if (!this._locale.wrap.contains(e.target)) this._closeLocale();
      }
      if (this._activeItem && !this.contains(e.target)) {
        this._closeAllMega();
      }
    });
  }
}

if (!customElements.get('hov-mega-header')) {
  customElements.define('hov-mega-header', HovMegaHeader);
}
